import { useState } from 'react';
import { DEFAULT_ENTITY_COLOR } from '../../utils/entityColor';
import { X, Plus, Trash2, Edit2, GripVertical, Settings2 } from 'lucide-react';
import usePropertyStore from '../../stores/usePropertyStore';
import CustomSelect from '../ui/CustomSelect';
import './PropertyManagerModal.css';

const PROPERTY_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'url', label: 'URL' },
    { value: 'select', label: 'Select (Single)' },
    { value: 'multi-select', label: 'Multi-Select' }
];

export default function PropertyManagerModal({ isOpen, onClose }) {
    const properties = usePropertyStore(s => s.properties);
    const addProperty = usePropertyStore(s => s.addProperty);
    const updateProperty = usePropertyStore(s => s.updateProperty);
    const deleteProperty = usePropertyStore(s => s.deleteProperty);

    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    
    // For new option input
    const [newOptionLabel, setNewOptionLabel] = useState('');

    if (!isOpen) return null;

    const handleCreateProperty = () => {
        const id = addProperty({ name: 'New Property', type: 'text' });
        const prop = usePropertyStore.getState().properties.find(p => p.id === id);
        setEditDraft(prop);
        setEditingId(id);
    };

    const handleEditProperty = (prop) => {
        setEditingId(prop.id);
        setEditDraft({ ...prop });
    };

    const handleSaveProperty = () => {
        if (editDraft) {
            updateProperty(editDraft.id, editDraft);
        }
        setEditingId(null);
        setEditDraft(null);
    };

    const handleDeleteProperty = (id) => {
        if (window.confirm("Delete this property? This won't remove local values from tasks/projects, but will remove the schema globally.")) {
            deleteProperty(id);
            if (editingId === id) {
                setEditingId(null);
                setEditDraft(null);
            }
        }
    };

    const handleAddOption = (e) => {
        e.preventDefault();
        if (!newOptionLabel.trim()) return;
        
        const newOption = {
            id: crypto.randomUUID(),
            label: newOptionLabel.trim(),
            color: DEFAULT_ENTITY_COLOR // Default color
        };
        
        setEditDraft({
            ...editDraft,
            options: [...(editDraft.options || []), newOption]
        });
        setNewOptionLabel('');
    };

    const handleDeleteOption = (optId) => {
        setEditDraft({
            ...editDraft,
            options: editDraft.options.filter(o => o.id !== optId)
        });
    };

    return (
        <div className="property-modal-overlay" onClick={onClose}>
            <div className="property-modal animate-scale-in" onClick={e => e.stopPropagation()}>
                <div className="property-modal-header">
                    <h2><Settings2 size={20} /> Manage Properties</h2>
                    <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
                </div>
                
                <div className="property-modal-body">
                    <div className="property-list">
                        <div className="property-list-header">
                            <h3>Global Properties ({properties.length})</h3>
                            <button className="btn btn-ghost btn-sm" onClick={handleCreateProperty}>
                                <Plus size={14} /> Add Property
                            </button>
                        </div>
                        
                        {properties.length === 0 && (
                            <div className="property-list-empty">
                                No properties defined. Create one to add custom fields to Projects and Tasks.
                            </div>
                        )}

                        <div className="property-items">
                            {properties.map(prop => (
                                <div key={prop.id} className={`property-item ${editingId === prop.id ? 'active' : ''}`}>
                                    <div className="property-item-main" onClick={() => handleEditProperty(prop)}>
                                        <GripVertical size={14} className="text-muted" />
                                        <span className="property-item-name">{prop.name}</span>
                                        <span className="property-item-type">{PROPERTY_TYPES.find(t => t.value === prop.type)?.label}</span>
                                    </div>
                                    <div className="property-item-actions">
                                        <button className="btn btn-ghost btn-icon btn-sm danger" onClick={(e) => { e.stopPropagation(); handleDeleteProperty(prop.id); }}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="property-editor">
                        {editingId && editDraft ? (
                            <div className="property-edit-form animate-fade-in">
                                <h3>Edit Property</h3>
                                
                                <div className="form-group">
                                    <label>Name</label>
                                    <input 
                                        type="text" 
                                        className="input-field" 
                                        value={editDraft.name}
                                        onChange={e => setEditDraft({ ...editDraft, name: e.target.value })}
                                        placeholder="e.g., Client, Budget, Tech Stack"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Type</label>
                                    <CustomSelect 
                                        className="input-field"
                                        value={editDraft.type}
                                        onChange={val => setEditDraft({ ...editDraft, type: val })}
                                        options={PROPERTY_TYPES}
                                    />
                                </div>

                                {(editDraft.type === 'select' || editDraft.type === 'multi-select') && (
                                    <div className="form-group options-group">
                                        <label>Options</label>
                                        <div className="options-list">
                                            {(editDraft.options || []).map(opt => (
                                                <div key={opt.id} className="option-item">
                                                    <input 
                                                        type="color" 
                                                        className="option-color"
                                                        value={opt.color}
                                                        onChange={(e) => {
                                                            const newOpts = editDraft.options.map(o => o.id === opt.id ? { ...o, color: e.target.value } : o);
                                                            setEditDraft({ ...editDraft, options: newOpts });
                                                        }}
                                                    />
                                                    <input 
                                                        type="text" 
                                                        className="input-field flex-1"
                                                        value={opt.label}
                                                        onChange={(e) => {
                                                            const newOpts = editDraft.options.map(o => o.id === opt.id ? { ...o, label: e.target.value } : o);
                                                            setEditDraft({ ...editDraft, options: newOpts });
                                                        }}
                                                    />
                                                    <button className="btn btn-ghost btn-icon danger" onClick={() => handleDeleteOption(opt.id)}>
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <form className="option-add-form" onSubmit={handleAddOption}>
                                            <input 
                                                type="text" 
                                                className="input-field"
                                                placeholder="Add option..."
                                                value={newOptionLabel}
                                                onChange={e => setNewOptionLabel(e.target.value)}
                                            />
                                            <button type="submit" className="btn btn-ghost btn-sm">Add</button>
                                        </form>
                                    </div>
                                )}

                                <div className="property-edit-actions">
                                    <button className="btn btn-primary" onClick={handleSaveProperty}>Save Changes</button>
                                </div>
                            </div>
                        ) : (
                            <div className="property-editor-empty">
                                Select a property to edit its details.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
